/* eslint-disable jsx-a11y/media-has-caption */
import React, { useRef, useEffect, useContext, Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PanZoom } from 'react-easy-panzoom';
import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { get } from 'lodash';
import './styles.css';

import AppContext from '../../context/AppContext';
import PageContext from '../../context/PageContext';

import LeftSidebar from '../LeftSidebar/LeftSidebar';

import templates from '../../templates';
import PageController from '../../shared/PageController';
import PrintDialog from '../../shared/PrintDialog';

import Header from '../../shared/header/Header';

import { changeLoggedIn } from '../../actions';
import RightSidebar from '../RightSidebar/RightSidebar';
import {
  isUserRoleAvailable,
  roles,
  importData,
  setSelectedUserId,
  setSelectedCvId,
  setResumeItems,
  changeSpinnerValue,
} from '../../utils';
import { getCVByUsername } from '../../services/api';
import { infoToast, errorToast } from '../../shared/toast';

export const App = () => {
  const pageRef = useRef(null);
  const panZoomRef = useRef(null);
  const { i18n } = useTranslation();

  const [scaleValue, setScaleValue] = useState(1);
  const [lastCvUpdate, setLastCvUpdate] = useState(1);

  const context = useContext(AppContext);
  const { state, dispatch } = context;
  const { theme, settings, data, userRoles, ownUserInfo } = state;
  const location = useLocation();

  const isRightBarAvailable = isUserRoleAvailable(userRoles, [
    roles.BENCH_MANAGER,
    roles.LEAD_SALE_MANAGER,
    roles.LEAD_SALE_MANAGER_READONLY,
    roles.PROJECT_MANAGER,
    roles.RECRUITER,
    roles.LEAD_RECRUITER,
    roles.RESEARCHER,
    roles.SUPERUSER,
    roles.CTO,
    roles.PEOPLE_MANAGER,
    roles.DEPUTY_PEOPLE_MANAGER,
    roles.TEAM_MANAGER,
    // roles.SALE_MANAGER, - исключен, не видит правый сайдбар
  ]);

  const [showTab, setShowTab] = useState(!isRightBarAvailable);

  const pageContext = useContext(PageContext);
  const { setPageRef, setPanZoomRef } = pageContext;

  useEffect(() => {
    setPageRef(pageRef);
    setPanZoomRef(panZoomRef);
    i18n.changeLanguage(settings.language);
  }, [dispatch, setPageRef, setPanZoomRef, i18n, settings.language]);

  const canViewOtherResumes =
    isRightBarAvailable ||
    isUserRoleAvailable(userRoles, [roles.SALE_MANAGER, roles.AUTHORIZED]);

  useEffect(() => {
    const { search } = location;
    const query = new URLSearchParams(search);
    const username = query.get('username');
    const resumeId = query.get('resumeId');

    if (username && ownUserInfo?.username && canViewOtherResumes) {
      changeSpinnerValue(dispatch, true);
      (async () => {
        try {
          const res = await getCVByUsername(username);
          if (Array.isArray(res) && res.length > 0) {
            const byId = resumeId && res.find((item) => item.id === resumeId);
            const main = res.find((item) => item.isMain === true);
            const selected = byId || main || res[0];

            if (resumeId && !byId) {
              infoToast(`CV ${resumeId} is not found for ${username}, main CV is opened`);
            }

            const cvItem = { data: { ...selected } };
            const cvId = get(cvItem.data, 'id');
            const userId = get(cvItem.data, 'user._id');
            setResumeItems(dispatch, res);
            setSelectedCvId(dispatch, cvId);
            setSelectedUserId(dispatch, userId);
            importData(dispatch, cvItem);
          } else {
            errorToast(`CV is not found for user ${username}`);
          }
        } catch (error) {
          errorToast(`Error loading CV for ${username}: ${error.message}`);
        } finally {
          changeSpinnerValue(dispatch, false);
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, dispatch, ownUserInfo?.username]);

  const onStateChange = () => {
    if (panZoomRef.current) setScaleValue(panZoomRef.current.state.scale);
  };

  const calcColsNumberCenter = () => {
    let result = '';
    if (isRightBarAvailable) result = 'isRightbar';
    return result;
  };
  const colsNumberCenter = calcColsNumberCenter();
  const colsNumberLeftSideBar = showTab ? 'col-span-6' : 'col-span-1';

  return (
    <Suspense fallback="Loading...">
      <div className="h-screen grid grid-cols-21 items-center">
        <div
          className={`${colsNumberLeftSideBar} z-10 ${
            showTab ? 'showtab' : ''
          } LeftSidebar-container`}
        >
          <LeftSidebar showTab={showTab} setShowTab={setShowTab} />
        </div>
        <div
          className={`z-10 ${colsNumberCenter} ${
            showTab ? 'showtab' : ''
          } centercontainer h-screen overflow-auto`}
        >
          <Header setLastCvUpdate={setLastCvUpdate} />
          <div className="flex justify-center items-center">
            <PanZoom
              ref={panZoomRef}
              minZoom="0.4"
              maxZoom="1"
              autoCenter
              autoCenterZoomLevel={1}
              enableBoundingBox
              boundaryRatioVertical={0.8}
              boundaryRatioHorizontal={0.8}
              style={{ outline: 'none' }}
              disabled
              onStateChange={onStateChange}
            >
              <div id="page" ref={pageRef} className="shadow-2xl break-words mb-4">
                {(() => {
                  const template = templates.find((x) => theme?.layout?.toLowerCase() === x.key);
                  return template?.component ? template.component() : null;
                })()}
              </div>
            </PanZoom>
            <PageController scaleValue={scaleValue} />
          </div>
        </div>
        <div id="printPage" className="break-words">
          {(() => {
            const template = templates.find((x) => theme?.layout?.toLowerCase() === x.key);
            return template?.component ? template.component() : null;
          })()}
        </div>
        {isRightBarAvailable ? (
          <div className={`${showTab ? 'showtab' : ''} rightsidebar z-10`}>
            <RightSidebar lastCvUpdate={lastCvUpdate} />
          </div>
        ) : null}
        <PrintDialog
          firstName={data?.summary?.firstName || ''}
          lastName={data?.summary?.lastName || ''}
          position={data?.summary?.position || ''}
        />
      </div>
    </Suspense>
  );
};

export default connect(
  (state) => ({
    loggedIn: state.loggedIn,
  }),
  {
    changeLoggedIn,
  },
)(App);
